import { openSearchPanel } from "@codemirror/search";
import { dependenciesLock, lockPathRecord, type IdfTargets } from "idfweb-build";
import {
    dependencyProblem,
    emptyDependency,
    lockedComponent,
    parseLock,
    parseManifest,
    putDependency,
    registryPage,
    removeDependency,
    resolvedName,
    setIdf,
    setMetadata,
    textEdit,
    type Condition,
    type Dependency,
    type Lock,
    type LockedComponent,
    type Manifest,
    type MetadataList,
    type MetadataText,
    type Registry,
    type RegistryHit,
    type Require,
    type Result,
    type Source,
} from "idfweb-components";
import { Filetype } from "idfweb-vfs";

import type { FileView } from "./editors.ts";
import {
    button,
    chips,
    combo,
    commitOnLeave,
    externalLink,
    field,
    h,
    icon,
    iconButton,
    textInput,
    type ComboOption,
} from "./manifest-widgets.ts";
import type { RuntimeLink } from "./runtime.ts";
import type { ReplaceEdit } from "./search.ts";

/** What the manifest editor needs from the workbench. */
export interface ManifestHost {
    runtime: RuntimeLink;
    registry: Registry;
    /** The file's text, as edited if it is open. */
    text(path: string): Promise<string | null>;
    /** Apply edits through the file's editor, as one undoable change; resolves to how many applied. */
    replace(path: string, edits: ReplaceEdit[]): Promise<number>;
    save(path: string): Promise<void>;
    isReadOnly(path: string): boolean;
    projectRoot(): string;
    /** Where builds of the project root go, where the configure records the lock file's path. */
    buildDir(): string;
    /** Configure the project root again, which resolves its dependencies; resolves to the error, if any. */
    reconfigure(): Promise<string | undefined>;
    targets(): IdfTargets | undefined;
}

/**
 * The idf_component.yml editor: the component's metadata and its dependencies
 * in a form, each dependency with the components it brings in as the project's
 * dependencies.lock resolved them. Each change is written to the file at once,
 * through the file's editor. The tab's text mode is the file itself.
 */
export function manifestView(host: ManifestHost): FileView {
    return {
        matches: isManifest,
        byDefault: true,
        open: { title: "Open Manifest Editor", icon: "package" },
        close: { title: "Open as Text", icon: "go-to-file" },
        create: (path, saved) => new ManifestEditor(host, path, saved).handle(),
        textShown: (view) => {
            openSearchPanel(view);
        },
    };
}

export function isManifest(path: string | undefined): boolean {
    return path !== undefined && path.slice(path.lastIndexOf("/") + 1) === "idf_component.yml";
}

const LICENSES = [
    "Apache-2.0",
    "MIT",
    "BSD-2-Clause",
    "BSD-3-Clause",
    "GPL-2.0-only",
    "LGPL-2.1-or-later",
    "Unlicense",
];
const CONDITION_HINTS = [
    "target in [esp32s3, esp32p4]",
    "target not in [esp32c2]",
    "idf_version >=5.4",
    "$CONFIG{BT_ENABLED} == True",
    "$ENV_VAR == value",
];
const SOURCE_ICON: Record<Source | "idf", string> = { registry: "package", git: "repo", path: "folder", idf: "chip" };
const SOURCE_TITLE: Record<Source, string> = { registry: "Registry", git: "Git", path: "Local" };

function dirname(path: string): string {
    return path.slice(0, path.lastIndexOf("/")) || "/";
}

/** `to` relative to the directory `from`, both absolute guest paths. */
export function relativePath(from: string, to: string): string {
    const a = from.split("/").filter(Boolean);
    const b = to.split("/").filter(Boolean);
    let common = 0;
    while (common < a.length && common < b.length && a[common] === b[common]) common++;
    return [...a.slice(common).map(() => ".."), ...b.slice(common)].join("/") || ".";
}

function copyDependency(dep: Dependency): Dependency {
    const copy = (list: Condition[]) => list.map((condition) => ({ ...condition }));
    return { ...dep, rules: copy(dep.rules), matches: copy(dep.matches) };
}

function changed(a: Dependency, b: Dependency): boolean {
    return JSON.stringify(a) !== JSON.stringify(b);
}

class ManifestEditor {
    readonly element = h("div", { class: "wb-mf" });
    readonly #host: ManifestHost;
    readonly #path: string;
    readonly #readOnly: boolean;
    #manifest: Manifest | undefined;
    #error: string | undefined;
    /** A change the file refused, shown until the next one. */
    #notice: string | undefined;
    #lock: Lock | undefined;
    #lockFile = "";
    /** Whether the manifest changed after dependencies.lock was written. */
    #stale = false;
    #resolving = false;
    /** The dependency whose details are open, as it was read, and the form's copy of it. */
    #editing: { name: string; original: Dependency; draft: Dependency; problem?: string } | undefined;
    #adding: { draft: Dependency; problem?: string; searched?: boolean } | undefined;
    /** A row for a new maintainer, shown until its text is written. */
    #newMaintainer = false;
    /** The manifest's modification time when a configure of the project last finished. */
    #configured = 0;
    /** The state the form was last drawn from, so a load that changes nothing leaves it alone. */
    #drawn = "";
    readonly #expanded = new Set<string>();
    #metadataOpen: boolean | undefined;
    #loading: Promise<void> = Promise.resolve();
    #reload = false;
    #writing: Promise<unknown> = Promise.resolve();
    #disposed = false;
    /** A drawing put off while a field's list is open, done when the focus leaves the field. */
    #deferred = false;
    readonly #unsubscribe: () => void;

    constructor(host: ManifestHost, path: string, saved: Promise<void>) {
        this.#host = host;
        this.#path = path;
        this.#readOnly = host.isReadOnly(path) || path.includes("/managed_components/");
        this.element.append(h("div", { class: "wb-mf-message" }, "Loading the manifest…"));
        this.element.addEventListener("focusout", () => {
            if (!this.#deferred) return;
            this.#deferred = false;
            queueMicrotask(() => this.#render());
        });
        void saved.catch(() => {}).then(() => this.#load());
        this.#unsubscribe = host.runtime.on((message) => {
            if (message.type === "changed") {
                const watched = [path, this.#lockFile, lockPathRecord(host.buildDir())];
                if (message.files.some((file) => watched.includes(file))) this.#load();
            } else if (message.type === "configured") {
                void this.#markConfigured().then(() => this.#load());
            } else if (message.type === "project-root" || message.type === "built") {
                this.#load();
            }
        });
    }

    handle(): { element: HTMLElement; dispose: () => void } {
        return {
            element: this.element,
            dispose: () => {
                this.#disposed = true;
                this.#unsubscribe();
            },
        };
    }

    /** The lock file the project's configure uses, as it recorded it, or the default before any configure. */
    async #lockPath(): Promise<string> {
        const record = await this.#host.runtime.fs.readFile(lockPathRecord(this.#host.buildDir())).then(
            (bytes) => new TextDecoder().decode(bytes),
            () => undefined,
        );
        return dependenciesLock(this.#host.projectRoot(), record);
    }

    /** Read the manifest and the lock again; loads asked for during one run once after it. */
    #load(): void {
        if (this.#reload) return;
        this.#reload = true;
        this.#loading = this.#loading.then(async () => {
            this.#reload = false;
            if (this.#disposed) return;
            await this.#read();
            const state = JSON.stringify([
                this.#manifest,
                this.#error,
                [...(this.#lock?.components ?? [])],
                this.#stale,
            ]);
            if (state === this.#drawn) return;
            this.#drawn = state;
            this.#render();
        });
    }

    /** A configure read the manifest as it is now, and the lock it left holds the solution for it. */
    async #markConfigured(): Promise<void> {
        const stat = await this.#host.runtime.fs.stat(this.#path).catch(() => undefined);
        if (stat !== undefined) this.#configured = stat.mtimeMs;
    }

    async #read(): Promise<void> {
        const fs = this.#host.runtime.fs;
        // The file on disk: the editor takes a change made elsewhere only after it has read it too.
        const text = await fs.readFile(this.#path).then(
            (bytes) => new TextDecoder().decode(bytes),
            () => null,
        );
        if (text === null) {
            this.#manifest = undefined;
            this.#error = `${this.#path} cannot be read.`;
            return;
        }
        const parsed = parseManifest(text);
        this.#manifest = parsed.ok ? parsed.value : undefined;
        this.#error = parsed.ok ? undefined : `${parsed.error} Open it as text to fix it.`;
        if (this.#metadataOpen === undefined && parsed.ok) {
            const m = parsed.value.metadata;
            this.#metadataOpen = Object.values(m).some((value) => value.length > 0);
        }
        const lockPath = await this.#lockPath();
        this.#lockFile = lockPath;
        const [lockText, lockStat, manifestStat] = await Promise.all([
            fs.readFile(lockPath).then(
                (bytes) => new TextDecoder().decode(bytes),
                () => undefined,
            ),
            fs.stat(lockPath).catch(() => undefined),
            fs.stat(this.#path).catch(() => undefined),
        ]);
        this.#lock = lockText === undefined ? undefined : parseLock(lockText);
        // The component manager leaves a lock that would not change as it is, so a configure counts too.
        this.#stale =
            lockStat !== undefined &&
            manifestStat !== undefined &&
            manifestStat.mtimeMs > Math.max(lockStat.mtimeMs, this.#configured);
    }

    /** Whether the lock holds a solution for the manifest as it is. */
    #isResolved(): boolean {
        const deps = this.#manifest?.dependencies ?? [];
        if (this.#lock === undefined) return deps.length === 0;
        return !this.#stale && deps.every((dep) => this.#status(dep) !== "unresolved");
    }

    #status(dep: Dependency): LockedComponent | "skipped" | "unresolved" {
        const locked = lockedComponent(this.#lock, dep);
        if (locked !== undefined) return locked;
        // A dependency whose conditions are false for the lock's target is left out of the solution.
        const conditional = dep.rules.length + dep.matches.length > 0;
        return this.#lock !== undefined && !this.#stale && conditional ? "skipped" : "unresolved";
    }

    /**
     * Apply `change` to the file's text through its editor and save it. A
     * change that cannot be made, or text that changed meanwhile, is reported
     * above the form and nothing is written.
     */
    #write(change: (text: string) => Result<string>): Promise<boolean> {
        const run = this.#writing.then(async () => {
            const before = await this.#host.text(this.#path);
            if (before === null) return this.#refuse(`${this.#path} cannot be read.`);
            const after = change(before);
            if (!after.ok) return this.#refuse(after.error);
            this.#notice = undefined;
            if (after.value === before) return true;
            const applied = await this.#host.replace(this.#path, [textEdit(before, after.value)]);
            if (applied === 0) return this.#refuse("The file changed while the change was made. Try again.");
            await this.#host.save(this.#path);
            this.#load();
            return true;
        });
        this.#writing = run.catch(() => {});
        return run;
    }

    #refuse(error: string): false {
        this.#notice = error;
        this.#drawn = "";
        this.#render();
        return false;
    }

    async #resolve(): Promise<void> {
        if (this.#resolving) return;
        this.#resolving = true;
        this.#notice = undefined;
        this.#render();
        await this.#writing;
        await this.#host.save(this.#path);
        const error = await this.#host.reconfigure();
        this.#resolving = false;
        if (error !== undefined) this.#notice = `The dependencies could not be resolved: ${error}`;
        else await this.#markConfigured();
        this.#drawn = "";
        this.#load();
    }

    // ---- drawing --------------------------------------------------------------

    #render(): void {
        if (this.#disposed) return;
        // Drawing the form again would close the list and drop the choices it has not written yet.
        const open = document.activeElement
            ?.closest(".wb-mf-combo, .wb-mf-chips")
            ?.querySelector(".wb-mf-popup:not([hidden])");
        if (open && this.element.contains(open)) {
            this.#deferred = true;
            return;
        }
        const scroll = this.element.querySelector(".wb-mf-content")?.scrollTop ?? 0;
        const active = document.activeElement;
        const focused =
            active instanceof HTMLElement && this.element.contains(active) ? active.dataset["key"] : undefined;
        const editable =
            active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement ? active : undefined;
        const typed = editable?.dataset["commit"] !== undefined ? editable.value : undefined;
        const selection = editable ? [editable.selectionStart, editable.selectionEnd] : undefined;
        const children: Node[] = [];
        if (this.#notice !== undefined) {
            children.push(h("div", { class: "wb-mf-notice", role: "alert" }, icon("error"), ` ${this.#notice}`));
        }
        if (this.#manifest === undefined) {
            children.push(h("div", { class: "wb-mf-message" }, this.#error ?? "Loading the manifest…"));
            this.element.replaceChildren(...children);
            return;
        }
        const content = h(
            "div",
            { class: "wb-mf-content" },
            h(
                "div",
                { class: "wb-mf-inner" },
                this.#header(),
                this.#metadataSection(this.#manifest),
                this.#dependencySection(this.#manifest),
            ),
        );
        children.push(content);
        this.element.replaceChildren(...children);
        content.scrollTop = scroll;
        if (focused !== undefined) {
            const target = this.element.querySelector<HTMLElement>(`[data-key="${CSS.escape(focused)}"]`);
            // Text typed and not yet written stays; the field writes it when it is left.
            target?.focus();
            if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
                if (typed !== undefined) target.value = typed;
                if (selection?.[0] != null) target.setSelectionRange(selection[0], selection[1] ?? selection[0]);
            }
        }
    }

    #header(): HTMLElement {
        const component = this.#path.split("/").at(-2) ?? "";
        let status: HTMLElement;
        if (this.#resolving) {
            status = h(
                "span",
                { class: "wb-mf-resolve wb-mf-dim" },
                "Resolving dependencies",
                icon("loading", "codicon-modifier-spin"),
            );
        } else if (this.#isResolved()) {
            const target = this.#lock?.target;
            status = h(
                "button",
                {
                    type: "button",
                    class: "wb-mf-resolve wb-mf-resolved-state",
                    title: target === undefined ? undefined : `Resolved for ${target}`,
                },
                "Dependencies resolved",
                icon("check"),
            );
            status.addEventListener("click", () => void this.#resolve());
        } else {
            status = h("button", { type: "button", class: "wb-mf-resolve" }, "Resolve dependencies", icon("refresh"));
            status.addEventListener("click", () => void this.#resolve());
        }
        return h(
            "div",
            { class: "wb-mf-header" },
            h(
                "div",
                { class: "wb-mf-header-title" },
                icon("package"),
                h("span", { class: "wb-mf-component" }, component),
                h(
                    "span",
                    { class: "wb-mf-dim" },
                    this.#readOnly ? "component manifest, read-only" : "component manifest",
                ),
            ),
            status,
        );
    }

    // ---- metadata ---------------------------------------------------------------

    #metadataSection(manifest: Manifest): HTMLElement {
        const m = manifest.metadata;
        const details = h("details", { class: "wb-mf-section", open: this.#metadataOpen === true });
        details.addEventListener("toggle", () => (this.#metadataOpen = details.open));
        const save = (key: MetadataText | MetadataList, value: string | string[]) =>
            void this.#write((text) => setMetadata(text, key, value));
        const text = (key: MetadataText, title: string, placeholder: string) =>
            field(title, textInput(m[key], placeholder, `meta-${key}`, { change: (value) => save(key, value) }));
        const description = h("textarea", { rows: "2", "data-key": "meta-description" });
        description.value = m.description;
        commitOnLeave(description, m.description, (value) => save("description", value));
        const license = combo({
            value: m.license,
            placeholder: "SPDX identifier",
            key: "meta-license",
            options: (query) =>
                LICENSES.filter((name) => name.toLowerCase().includes(query.toLowerCase())).map((value) => ({ value })),
            onPick: (value) => save("license", value),
            onChange: (value) => save("license", value),
        });
        const targets = this.#host.targets();
        const fields = h(
            "fieldset",
            { class: "wb-mf-fields", disabled: this.#readOnly },
            text("version", "Version", "1.0.0"),
            field("Description", description),
            field("License", license),
            field(
                "Targets",
                chips({
                    values: m.targets,
                    placeholder: "",
                    empty: "All targets",
                    key: "meta-targets",
                    options: [...(targets?.supported ?? []), ...(targets?.preview ?? [])],
                    onChange: (values) => save("targets", values),
                    disabled: this.#readOnly,
                }),
            ),
            field(
                "Tags",
                chips({
                    values: m.tags,
                    placeholder: "Add a tag",
                    key: "meta-tags",
                    onChange: (values) => save("tags", values),
                    disabled: this.#readOnly,
                }),
            ),
            field("Maintainers", this.#maintainers(m.maintainers)),
            h("div", { class: "wb-mf-subheading" }, "Links"),
            text("url", "Website", "https://…"),
            text("repository", "Repository", "https://github.com/…/….git"),
            text("documentation", "Documentation", "https://…"),
            text("issues", "Issue Tracker", "https://…"),
            text("discussion", "Discussion", "https://…"),
        );
        details.append(
            h("summary", { class: "wb-mf-section-title" }, icon("chevron-right", "wb-mf-twisty"), "Component Metadata"),
            fields,
        );
        return details;
    }

    #maintainers(list: string[]): HTMLElement {
        // Each change applies to the list as the file holds it when the change is written.
        const save = (update: (current: string[]) => string[]) =>
            void this.#write((text) => {
                const parsed = parseManifest(text);
                if (!parsed.ok) return parsed;
                const next = update(parsed.value.metadata.maintainers).filter((item) => item.trim() !== "");
                return setMetadata(text, "maintainers", next);
            });
        const row = (value: string, index: number) =>
            h(
                "div",
                { class: "wb-mf-list-row" },
                textInput(value, "Name <email@example.com>", `maintainer-${index}`, {
                    change: (next) => {
                        if (index === list.length) this.#newMaintainer = false;
                        save((current) =>
                            index < current.length
                                ? current.map((item, i) => (i === index ? next : item))
                                : [...current, next],
                        );
                    },
                }),
                !this.#readOnly &&
                    iconButton("close", "Remove", () => {
                        if (index === list.length) {
                            this.#newMaintainer = false;
                            this.#render();
                        } else save((current) => current.filter((_, i) => i !== index));
                    }),
            );
        const rows = list.map(row);
        if (this.#newMaintainer) rows.push(row("", list.length));
        const add = h("button", { type: "button", class: "wb-mf-link-button" }, icon("add"), " Add Maintainer");
        add.addEventListener("click", () => {
            this.#newMaintainer = true;
            this.#render();
            this.element.querySelector<HTMLElement>(`[data-key="maintainer-${list.length}"]`)?.focus();
        });
        return h("div", { class: "wb-mf-list" }, ...rows, !this.#readOnly && !this.#newMaintainer && add);
    }

    // ---- dependencies -----------------------------------------------------------

    #dependencySection(manifest: Manifest): HTMLElement {
        const add = button(
            "Add Dependency",
            () => {
                if (this.#busy()) return;
                this.#adding = { draft: emptyDependency() };
                this.#editing = undefined;
                this.#render();
                this.element.querySelector<HTMLElement>('.wb-mf-add [data-key="dep-name"]')?.focus();
                this.#adding.searched = true;
            },
            true,
        );
        return h(
            "section",
            { class: "wb-mf-section wb-mf-deps" },
            h(
                "div",
                { class: "wb-mf-section-title wb-mf-section-bar" },
                "Dependencies",
                h("span", { class: "wb-mf-count" }, String(manifest.dependencies.length)),
                h("span", { class: "wb-mf-spacer" }),
                !this.#readOnly && this.#adding === undefined && add,
            ),
            this.#adding !== undefined && this.#addPanel(this.#adding),
            h(
                "div",
                { class: "wb-mf-dep-list", role: "tree" },
                h(
                    "div",
                    { class: "wb-mf-dep wb-mf-dep-head", role: "presentation" },
                    h("span", {}),
                    h("span", {}),
                    h("span", {}, "Component"),
                    h("span", {}, "Requested"),
                    h("span", {}, "Resolved"),
                    h("span", {}),
                    h("span", {}),
                ),
                this.#idfRow(manifest),
                ...manifest.dependencies.map((dep) => this.#dependencyRow(dep)),
            ),
        );
    }

    #idfRow(manifest: Manifest): HTMLElement {
        const spec = textInput(manifest.idf ?? "", "any", "idf", {
            change: (value) => void this.#write((text) => setIdf(text, value)),
        });
        spec.classList.add("wb-mf-spec");
        spec.disabled = this.#readOnly;
        const idf = this.#lock?.components.get("idf");
        return h(
            "div",
            { class: "wb-mf-dep wb-mf-dep-idf", role: "treeitem" },
            h("span", {}),
            icon(SOURCE_ICON.idf, "wb-mf-source"),
            h("span", { class: "wb-mf-dep-name" }, "ESP-IDF"),
            h("span", { class: "wb-mf-dep-spec" }, spec),
            h("span", { class: "wb-mf-resolved" }, idf?.version ?? ""),
            h("span", {}),
            h("span", {}),
        );
    }

    #resolvedCell(dep: Dependency): HTMLElement {
        const status = this.#status(dep);
        if (status === "skipped") {
            const target = this.#lock?.target ?? "this target";
            return h(
                "span",
                { class: "wb-mf-resolved wb-mf-skipped", title: `Its conditions are false for ${target}` },
                `not on ${target}`,
            );
        }
        if (status === "unresolved") {
            return h("span", { class: "wb-mf-resolved" });
        }
        return h("span", { class: "wb-mf-resolved" }, status.version || "local");
    }

    #dependencyRow(dep: Dependency): HTMLElement {
        const status = this.#status(dep);
        const locked = typeof status === "object" ? status : undefined;
        const key = resolvedName(dep);
        const expandable = locked !== undefined && locked.requires.length > 0;
        const expanded = expandable && this.#expanded.has(key);
        const editing = this.#editing?.name === dep.name ? this.#editing : undefined;
        const row = h(
            "div",
            {
                class: `wb-mf-dep${editing ? " wb-mf-dep-editing" : ""}${this.#readOnly ? " wb-mf-dep-static" : ""}`,
                role: "treeitem",
                "aria-expanded": expandable ? String(expanded) : undefined,
                tabindex: "0",
            },
            expandable
                ? iconButton(expanded ? "chevron-down" : "chevron-right", expanded ? "Collapse" : "Expand", () => {
                      if (expanded) this.#expanded.delete(key);
                      else this.#expanded.add(key);
                      this.#render();
                  })
                : h("span", {}),
            h("span", { title: SOURCE_TITLE[dep.source] }, icon(SOURCE_ICON[dep.source], "wb-mf-source")),
            h("span", { class: "wb-mf-dep-name", title: dep.name }, dep.name),
            h("span", { class: "wb-mf-dep-spec", title: this.#specText(dep) }, this.#specText(dep)),
            this.#resolvedCell(dep),
            h("span", { class: "wb-mf-badges" }, ...this.#badges(dep)),
            h(
                "span",
                { class: "wb-mf-actions" },
                this.#openLink(dep.source, dep.source === "git" ? dep.git : key),
                !this.#readOnly && iconButton("edit", "Edit", () => this.#openEdit(dep)),
                !this.#readOnly &&
                    iconButton("trash", "Remove", () => {
                        if (editing) this.#editing = undefined;
                        void this.#write((text) => removeDependency(text, dep.name));
                    }),
            ),
        );
        if (!this.#readOnly) {
            row.addEventListener("click", () => this.#openEdit(dep));
            row.addEventListener("keydown", (event) => {
                if (event.target === row && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    this.#openEdit(dep);
                }
            });
        }
        return h(
            "div",
            { class: "wb-mf-rows" },
            row,
            editing !== undefined && this.#details(editing),
            expanded &&
                h(
                    "div",
                    { class: "wb-mf-rows", role: "group" },
                    ...locked!.requires.map((name) => this.#nestedRow(name, 1, new Set([locked!.name]))),
                ),
        );
    }

    #openLink(source: Source | "idf", target: string | undefined): HTMLElement | undefined {
        if (target === undefined) return undefined;
        if (source === "registry") return externalLink(registryPage(target), "Open in the registry");
        if (source === "git" && /^https?:\/\//.test(target)) {
            return externalLink(target.replace(/\.git$/, ""), "Open the repository");
        }
        return undefined;
    }

    #openEdit(dep: Dependency): void {
        if (this.#editing?.name === dep.name || this.#busy()) return;
        this.#editing = { name: dep.name, original: dep, draft: copyDependency(dep) };
        this.#adding = undefined;
        this.#render();
    }

    /** Whether the open details or new dependency have changes not yet written; says so in their panel. */
    #busy(): boolean {
        if (this.#editing !== undefined && changed(this.#editing.draft, this.#editing.original)) {
            this.#editing.problem = "Apply these changes with Done, or discard them, first.";
        } else if (this.#adding !== undefined && changed(this.#adding.draft, emptyDependency())) {
            this.#adding.problem = "Add this dependency, or cancel it, first.";
        } else return false;
        this.#render();
        return true;
    }

    async #finishEdit(): Promise<void> {
        const editing = this.#editing;
        const manifest = this.#manifest;
        if (editing === undefined || manifest === undefined) return;
        if (!changed(editing.draft, editing.original)) {
            this.#editing = undefined;
            this.#render();
            return;
        }
        const others = manifest.dependencies.filter((dep) => dep.name !== editing.name);
        editing.problem = dependencyProblem(editing.draft, others);
        if (editing.problem !== undefined) {
            this.#render();
            return;
        }
        if (await this.#write((text) => putDependency(text, editing.name, editing.draft))) {
            if (this.#editing === editing) this.#editing = undefined;
            this.#render();
        }
    }

    async #finishAdd(): Promise<void> {
        const adding = this.#adding;
        const manifest = this.#manifest;
        if (adding === undefined || manifest === undefined) return;
        adding.problem = dependencyProblem(adding.draft, manifest.dependencies);
        if (adding.problem !== undefined) {
            this.#render();
            return;
        }
        if (await this.#write((text) => putDependency(text, undefined, adding.draft))) {
            if (this.#adding === adding) this.#adding = undefined;
            this.#render();
        }
    }

    #specText(dep: Dependency): string {
        if (dep.source === "path") return dep.path;
        if (dep.source === "git")
            return `${dep.git.replace(/^https?:\/\//, "")}${dep.version ? ` @ ${dep.version}` : ""}`;
        return dep.version || "*";
    }

    #badges(dep: Dependency): HTMLElement[] {
        const badges: HTMLElement[] = [];
        if (dep.require === "public")
            badges.push(h("span", { class: "wb-mf-badge", title: "require: public" }, "public"));
        if (dep.require === "no")
            badges.push(h("span", { class: "wb-mf-badge", title: "require: no" }, "download only"));
        if (dep.preRelease) badges.push(h("span", { class: "wb-mf-badge", title: "pre_release: true" }, "pre-release"));
        if (dep.overridePath) {
            badges.push(h("span", { class: "wb-mf-badge", title: `override_path: ${dep.overridePath}` }, "override"));
        }
        const all = dep.rules.map((c) => c.if);
        const any = dep.matches.map((c) => c.if).join(" || ");
        if (any !== "") all.push(all.length > 0 && dep.matches.length > 1 ? `(${any})` : any);
        if (all.length > 0) {
            const text = all.join(" && ");
            badges.push(
                h("span", { class: "wb-mf-badge wb-mf-badge-condition", title: text }, icon("filter"), ` if ${text}`),
            );
        }
        return badges;
    }

    #nestedRow(name: string, depth: number, seen: Set<string>): HTMLElement {
        const locked = this.#lock?.components.get(name);
        const direct = this.#manifest?.dependencies.some((dep) => resolvedName(dep) === name) ?? false;
        const source = locked?.source ?? "registry";
        const row = h(
            "div",
            { class: "wb-mf-dep wb-mf-dep-nested", role: "treeitem", style: `--depth: ${depth}` },
            h("span", {}),
            icon(SOURCE_ICON[source], "wb-mf-source"),
            h("span", { class: "wb-mf-dep-name", title: name }, name),
            h("span", { class: "wb-mf-dep-spec" }),
            h("span", { class: "wb-mf-resolved" }, locked?.version ?? ""),
            h(
                "span",
                { class: "wb-mf-badges" },
                direct && h("span", { class: "wb-mf-badge wb-mf-badge-dim" }, "also direct"),
            ),
            h("span", { class: "wb-mf-actions" }, this.#openLink(source, source === "git" ? locked?.location : name)),
        );
        if (locked === undefined || seen.has(name) || locked.requires.length === 0) return row;
        const next = new Set(seen).add(name);
        return h(
            "div",
            { class: "wb-mf-rows" },
            row,
            ...locked.requires.map((child) => this.#nestedRow(child, depth + 1, next)),
        );
    }

    // ---- the fields of a dependency ----------------------------------------------

    #details(editing: { draft: Dependency; problem?: string }): HTMLElement {
        const panel = h("div", { class: "wb-mf-details" });
        panel.addEventListener("click", (event) => event.stopPropagation());
        panel.append(
            ...this.#fields(editing.draft, false),
            ...this.#problem(editing.problem),
            h(
                "div",
                { class: "wb-mf-details-buttons" },
                button("Done", () => void this.#finishEdit(), true),
                button("Discard Changes", () => {
                    this.#editing = undefined;
                    this.#render();
                }),
            ),
        );
        return panel;
    }

    #problem(problem: string | undefined): HTMLElement[] {
        return problem === undefined ? [] : [h("div", { class: "wb-mf-problem", role: "alert" }, problem)];
    }

    #addPanel(adding: { draft: Dependency; problem?: string }): HTMLElement {
        const panel = h("div", { class: "wb-mf-add wb-mf-details" });
        panel.append(
            h("div", { class: "wb-mf-add-title" }, "New Dependency"),
            ...this.#fields(adding.draft, true),
            ...this.#problem(adding.problem),
            h(
                "div",
                { class: "wb-mf-details-buttons" },
                button("Add", () => void this.#finishAdd(), true),
                button("Cancel", () => {
                    this.#adding = undefined;
                    this.#render();
                }),
            ),
        );
        return panel;
    }

    #fields(dep: Dependency, adding: boolean): HTMLElement[] {
        const fields: HTMLElement[] = [field("Source", this.#sourceTabs(dep))];
        if (dep.source === "registry") {
            fields.push(
                field("Component", this.#registryPicker(dep, adding)),
                field("Version", this.#versionPicker(dep)),
                field(
                    "Pre-releases",
                    this.#checkbox(dep.preRelease, "Allow pre-release versions", (v) => (dep.preRelease = v)),
                ),
            );
        } else if (dep.source === "git") {
            fields.push(
                field(
                    "Repository URL",
                    textInput(dep.git, "https://github.com/…/….git", "dep-git", { input: (v) => (dep.git = v) }),
                ),
                field("Path in Repository", textInput(dep.path, "/", "dep-path", { input: (v) => (dep.path = v) })),
                field(
                    "Branch, Tag or Commit",
                    textInput(dep.version, "default branch", "dep-version", { input: (v) => (dep.version = v) }),
                ),
                field(
                    "Component Name",
                    textInput(dep.name, "sensor_drivers", "dep-name", { input: (v) => (dep.name = v.trim()) }),
                ),
            );
        } else {
            fields.push(
                field("Path", this.#pathPicker(dep)),
                field(
                    "Component Name",
                    textInput(dep.name, "directory name", "dep-name", { input: (v) => (dep.name = v.trim()) }),
                ),
            );
        }
        const require = h("select", { class: "wb-form-select" });
        for (const [value, text] of [
            ["private", "Private (PRIV_REQUIRES)"],
            ["public", "Public (REQUIRES)"],
            ["no", "No (download only)"],
        ] as const) {
            require.append(h("option", { value, selected: (dep.require || "private") === value }, text));
        }
        // A manifest without `require` keeps none while private stays chosen.
        const written = dep.require;
        require.addEventListener("change", () => {
            const value = require.value as Require;
            dep.require = value === "private" && written === "" ? "" : value;
        });
        fields.push(field("Require", require), field("Conditions", this.#conditions(dep)));
        if (dep.source === "registry") {
            fields.push(
                h(
                    "details",
                    { class: "wb-mf-advanced", open: dep.overridePath !== "" || dep.registryUrl !== "" },
                    h("summary", {}, icon("chevron-right", "wb-mf-twisty"), " Advanced"),
                    field(
                        "Override Path",
                        textInput(dep.overridePath, "../../", "dep-override", { input: (v) => (dep.overridePath = v) }),
                    ),
                    field(
                        "Registry URL",
                        textInput(dep.registryUrl, "https://components.espressif.com", "dep-registry", {
                            input: (v) => (dep.registryUrl = v),
                        }),
                    ),
                ),
            );
        }
        return fields;
    }

    #sourceTabs(dep: Dependency): HTMLElement {
        return h(
            "div",
            { class: "wb-mf-segmented", role: "tablist" },
            ...(["registry", "git", "path"] as const).map((source) => {
                const tab = h(
                    "button",
                    {
                        type: "button",
                        role: "tab",
                        "aria-selected": String(dep.source === source),
                        class: dep.source === source ? "wb-mf-selected" : "",
                    },
                    icon(SOURCE_ICON[source]),
                    ` ${SOURCE_TITLE[source]}`,
                );
                tab.addEventListener("click", () => {
                    dep.source = source;
                    this.#render();
                });
                return tab;
            }),
        );
    }

    /** The component's name, completed from the registry as it is typed. */
    #registryPicker(dep: Dependency, adding: boolean): HTMLElement {
        const target = this.#lock?.target;
        const taken = (name: string) =>
            name !== resolvedName(dep) &&
            (this.#manifest?.dependencies ?? []).some(
                (other) => other.name !== this.#editing?.name && resolvedName(other) === name,
            );
        let timer: ReturnType<typeof setTimeout> | undefined;
        return combo({
            value: dep.name,
            placeholder: "Search the registry, or type namespace/name",
            key: "dep-name",
            icon: "search",
            // The list opens by itself once, when the new dependency's panel opens.
            openWhenEmpty: adding && this.#adding?.searched !== true,
            options: (query) =>
                new Promise<RegistryHit[]>((resolve) => {
                    // Typing waits for a pause before it asks the registry.
                    clearTimeout(timer);
                    timer = setTimeout(() => resolve(this.#host.registry.search(query)), query === "" ? 0 : 250);
                }).then((hits) => hits.map((hit) => this.#hitOption(hit, taken(hit.name), target))),
            onInput: (value) => (dep.name = value.trim()),
            onPick: (value) => {
                if (value !== dep.name || dep.version === "") {
                    dep.version = "";
                    void this.#host.registry.versions(value).then(
                        (versions) => {
                            const latest = versions.find((v) => !v.yanked && !v.version.includes("-"));
                            if (dep.name === value && dep.version === "" && latest !== undefined) {
                                dep.version = `^${latest.version}`;
                                this.#render();
                            }
                        },
                        () => {},
                    );
                }
                dep.name = value;
                this.#render();
                this.element.querySelector<HTMLElement>('.wb-mf-details [data-key="dep-version"]')?.focus();
            },
        });
    }

    #hitOption(hit: RegistryHit, taken: boolean, target: string | undefined): ComboOption {
        const unsupported = target !== undefined && hit.targets.length > 0 && !hit.targets.includes(target);
        return {
            value: hit.name,
            disabled: taken,
            content: [
                h(
                    "div",
                    { class: "wb-mf-hit" },
                    icon("package", "wb-mf-source"),
                    h(
                        "div",
                        { class: "wb-mf-hit-text" },
                        h(
                            "div",
                            {},
                            h("span", { class: "wb-mf-dep-name" }, hit.name),
                            h("span", { class: "wb-mf-dim" }, `  ${hit.latest}`),
                        ),
                        h("div", { class: "wb-mf-dim" }, hit.description),
                    ),
                    h(
                        "div",
                        { class: "wb-mf-hit-meta" },
                        taken && h("span", { class: "wb-mf-badge" }, "added"),
                        unsupported &&
                            h(
                                "span",
                                {
                                    class: "wb-mf-badge wb-mf-badge-warning",
                                    title: `Supports ${hit.targets.join(", ")}`,
                                },
                                icon("warning"),
                                ` not for ${target}`,
                            ),
                    ),
                ),
            ],
        };
    }

    #versionPicker(dep: Dependency): HTMLElement {
        const note = (text: string) => h("span", { class: "wb-mf-dim wb-mf-option-note" }, text);
        return combo({
            value: dep.version,
            placeholder: "*",
            key: "dep-version",
            mono: true,
            options: async () => {
                const name = resolvedName(dep);
                // A dependency from another registry has versions the public one does not know.
                const other = dep.registryUrl.trim() !== "" && !dep.registryUrl.includes("components.espressif.com");
                const all =
                    name.includes("/") && !other ? await this.#host.registry.versions(name).catch(() => []) : [];
                const versions = all.filter((v) => !v.yanked && (dep.preRelease || !v.version.includes("-")));
                const latest = versions[0]?.version;
                const options: ComboOption[] = [];
                if (latest !== undefined) {
                    const [major, minor] = latest.split(/[.~-]/);
                    options.push(
                        { value: `^${latest}`, content: [`^${latest}`, note(`${major}.x`)] },
                        { value: `~${major}.${minor}`, content: [`~${major}.${minor}`, note(`${major}.${minor}.x`)] },
                    );
                }
                options.push({ value: "*", content: ["*", note("any")] });
                for (const [index, v] of versions.entries()) {
                    options.push({ value: v.version, content: [v.version, index === 0 && note("latest")] });
                }
                return options;
            },
            onInput: (value) => (dep.version = value.trim()),
            onPick: (value) => (dep.version = value),
        });
    }

    /** A local dependency's directory, offered from the project's `components` and this component's neighbours. */
    #pathPicker(dep: Dependency): HTMLElement {
        const fs = this.#host.runtime.fs;
        const here = dirname(this.#path);
        const directories = async (dir: string) =>
            (await fs.readdir(dir).catch(() => []))
                .filter((entry) => entry.filetype === Filetype.DIRECTORY && !entry.name.startsWith("."))
                .map((entry) => `${dir}/${entry.name}`);
        return combo({
            value: dep.path,
            placeholder: "../components/my_component",
            key: "dep-path",
            options: async (query) => {
                const found = [
                    ...(await directories(`${this.#host.projectRoot()}/components`)),
                    ...(await directories(dirname(here))),
                ];
                const paths = [...new Set(found)]
                    .filter((dir) => dir !== here && !dir.endsWith("/managed_components") && !dir.endsWith("/build"))
                    .map((dir) => relativePath(here, dir));
                return paths.filter((path) => path.includes(query)).map((value) => ({ value }));
            },
            onInput: (value) => (dep.path = value.trim()),
            onPick: (value) => {
                dep.path = value;
                if (dep.name === "") {
                    dep.name = value.split("/").filter(Boolean).at(-1) ?? "";
                    this.#render();
                }
            },
        });
    }

    /**
     * The conditions: one list with a choice of all or any, or, for an entry
     * that has both, its `rules`, all of which must hold, and its `matches`.
     */
    #conditions(dep: Dependency): HTMLElement {
        const label = (text: string) => h("div", { class: "wb-mf-dim" }, text);
        if (dep.rules.length > 0 && dep.matches.length > 0) {
            return h(
                "div",
                { class: "wb-mf-conditions" },
                label("All of these hold"),
                ...this.#conditionList(dep, "rules"),
                label("and one of these holds"),
                ...this.#conditionList(dep, "matches"),
            );
        }
        const key = dep.matches.length > 0 ? "matches" : "rules";
        const kind = h("select", { class: "wb-form-select wb-mf-condition-kind" });
        kind.append(
            h("option", { value: "rules", selected: key === "rules" }, "all hold"),
            h("option", { value: "matches", selected: key === "matches" }, "one holds"),
        );
        kind.addEventListener("change", () => {
            const to = kind.value as "rules" | "matches";
            [dep[to], dep[key]] = [dep[key], []];
            this.#render();
        });
        return h(
            "div",
            { class: "wb-mf-conditions" },
            dep[key].length > 1 &&
                h("div", { class: "wb-mf-inline" }, h("span", { class: "wb-mf-dim" }, "Include when"), kind),
            ...this.#conditionList(dep, key),
        );
    }

    #conditionList(dep: Dependency, key: "rules" | "matches"): HTMLElement[] {
        const list = dep[key];
        const rows = list.map((condition, index) =>
            h(
                "div",
                { class: "wb-mf-condition" },
                h("span", { class: "wb-mf-dim" }, "if"),
                combo({
                    value: condition.if,
                    placeholder: "target in [esp32s3, esp32p4]",
                    key: `${key}-if-${index}`,
                    mono: true,
                    options: (query) =>
                        CONDITION_HINTS.filter((hint) => hint.includes(query)).map((value) => ({ value })),
                    onInput: (value) => (condition.if = value),
                    onPick: (value) => (condition.if = value),
                }),
                textInput(condition.version, "version", `${key}-version-${index}`, {
                    input: (v) => (condition.version = v),
                }),
                iconButton("close", "Remove condition", () => {
                    list.splice(index, 1);
                    this.#render();
                }),
            ),
        );
        const add = h("button", { type: "button", class: "wb-mf-link-button" }, icon("add"), " Add Condition");
        add.addEventListener("click", () => {
            list.push({ if: "", version: "" });
            this.#render();
            this.element.querySelector<HTMLElement>(`[data-key="${key}-if-${list.length - 1}"]`)?.focus();
        });
        return [...rows, add];
    }

    #checkbox(checked: boolean, text: string, onChange: (value: boolean) => void): HTMLElement {
        const box = h("input", { type: "checkbox", checked });
        box.addEventListener("change", () => onChange(box.checked));
        return h("label", { class: "wb-mf-check" }, box, text);
    }
}
